// src/features/auth/utils/redirectToApp.ts
// Desktop/Electron hand-back: when the portal is opened with
// ?app_callback=http://localhost:{port}/auth/callback, a successful sign-in
// or sign-up sends the session token back to the app instead of showing the
// web dashboard. Shared by LoginForm and RegisterForm.

import { User } from '../../../services/authService';

export function getAppCallback(search: string): string | null {
  return new URLSearchParams(search).get('app_callback');
}

/**
 * Redirect to the desktop app's callback URL with auth credentials.
 * Returns true when a redirect was issued (caller should stop navigating).
 */
export function redirectToApp(appCallback: string | null, user: User): boolean {
  if (!appCallback) return false;
  try {
    const token = localStorage.getItem('accessToken');
    if (!token) return false;
    const userB64 = btoa(
      JSON.stringify({ id: user.id, email: user.email, firstName: user.firstName, lastName: user.lastName }),
    );
    const separator = appCallback.includes('?') ? '&' : '?';
    window.location.href = `${appCallback}${separator}token=${encodeURIComponent(token)}&user=${encodeURIComponent(userB64)}`;
    return true;
  } catch {
    return false;
  }
}

/** Where to go after auth: the page that required sign-in, else the dashboard. */
export function resolveRedirect(state: unknown, search: string): string {
  const fromState = (state as { from?: { pathname?: string; search?: string } } | null)?.from;
  if (fromState?.pathname && fromState.pathname !== '/login' && fromState.pathname !== '/register') {
    return `${fromState.pathname}${fromState.search || ''}`;
  }
  const fromQuery = new URLSearchParams(search).get('from');
  if (fromQuery && fromQuery.startsWith('/') && !fromQuery.startsWith('//')) return fromQuery;
  return '/dashboard';
}

/**
 * Split a free-form "Your name" into the first/last pair the backend needs.
 * Falls back to the email local part so the field can stay optional.
 */
export function splitName(fullName: string, email: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
  }
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: parts[0] };
  }
  const local = (email.split('@')[0] || 'user').replace(/[._+-]+/g, ' ').trim();
  const words = local.split(' ').filter(Boolean);
  const cap = (w: string) => w.charAt(0).toUpperCase() + w.slice(1);
  const first = cap(words[0] || 'User');
  const last = words[1] ? cap(words[1]) : first;
  return { firstName: first, lastName: last };
}
